import React from 'react'
import { connect } from 'react-redux'
import { Formik, Form, Field, ErrorMessage } from 'formik'
import * as Yup from 'yup'
import { abandonedCampaignConstant as acc, generalConstant as gc } from 'config/constants'
import { arraysToObject } from 'lib/utils'
import { abandonedCampaignAction } from 'actions'
import CampaignMailCodeHelp from 'components/parts/campaign/CampaignMailCodeHelp'
import {
  Loading,
  FieldSingle,
  FieldCode,
  FieldGroup,
  Label,
  InputGroupAddon,
  HelpIcon,
  HintIcon,
  ErrorText,
} from 'lib/components'
import {
  Alert,
  Button,
  FormGroup,
  Input,
  FormText,
  UncontrolledCollapse,
  UncontrolledTooltip,
} from 'reactstrap'
import ManualLink from 'components/parts/manual/ManualLink'

// constants
const abandonedTypes = arraysToObject(acc.abandonedTypes, 2, 1)
const followingAbandonedTypes = [abandonedTypes.notPurchasedAfterCampaign, abandonedTypes.notViewedCartAfterCampaign]

const validationSchema = Yup.object().shape({
  name: Yup.string().required(),
  messageType: Yup.string().required(),
  abandonedType: Yup.number().required(),
  triggerCampaignId: Yup.number()
    .when('abandonedType', {
      is: abandonedType => (followingAbandonedTypes.includes(abandonedType)),
      then: Yup.number().required('該当する先行キャンペーンが存在しません'),
    }),
  purchaseHistory: Yup.string().required(),
  customerType: Yup.number().required(),
  gender: Yup.string().required(),
  schedule: Yup.number().required(),
  aiOptimizedStatus: Yup.bool().required(),
  breakTimeEachCustomer: Yup.number(),
  breakTimePurchasedCustomer: Yup.number(),
  showOutOfStock: Yup.bool().required(),
  resumeItemIsEmpty: Yup.bool().required(),
  stopIfEmailExist: Yup.bool()
    .when('messageType', {
      is: messageType => (messageType !== 'mail'),
      then: Yup.bool().required(),
    }),
  previewText: Yup.string(),
  urlParam: Yup.string(),
  style: Yup.string(),
  messages: Yup.string(),
  testAddress: Yup.string().matches(/^U[0-9a-f]{32}$/),
  subject: Yup.string(),
})

export const OldAbandonedCampaignForm = ({
  company, abandonedCampaign, item, submitAction, dispatch,
}) => {
  const isTestAccount = company.item.testAccount
  let isTestDeliverySubmit = false

  function preCampaignSelect(type) {
    if (!followingAbandonedTypes.includes(type)) return null

    const triggerCampaigns = abandonedCampaign.items.filter(item => {
      if (type === abandonedTypes.notPurchasedAfterCampaign) {
        return item.abandonedType === abandonedTypes.cart
      } else if (type === abandonedTypes.notViewedCartAfterCampaign) {
        return item.abandonedType === abandonedTypes.browser
      }
      return false
    }).map(item => ([item.name, item.id]))
    triggerCampaigns.unshift(['', undefined])

    return (
      <FormGroup>
        <Label require>先行キャンペーン</Label>
        <Field type="select" name="triggerCampaignId" className="custom-select" as={Input}>
          {triggerCampaigns.map((option, index) => (
            <option key={index} value={option[1]}>{option[0]}</option>
          ))}
        </Field>
        <ErrorMessage name="triggerCampaignId" component={ErrorText} />
        <FormText>ステップメッセージの対象となる1通⽬のキャンペーンを選択してください。</FormText>
      </FormGroup>
    )
  }

  return (
    <Loading isLoading={ abandonedCampaign.isFetching || company.isFetching }>
      <Formik
        initialValues={{
          name: item.name || '',
          // メッセージタイプはLINEで固定
          messageType: 'line',
          abandonedType: item.abandonedType || 1,
          triggerCampaignId: item.triggerCampaignId || undefined,
          purchaseHistory: item.purchaseHistory || 'any_purchased_count',
          customerType: item.customerType || 0,
          gender: item.gender || 'all_gender',
          stopIfEmailExist: item.stopIfEmailExist || false,
          schedule: item.schedule === 0 || item.schedule ? item.schedule : 30,
          // AI最適化設定はfalseで固定
          aiOptimizedStatus: false,
          breakTimeEachCustomer: item.breakTimeEachCustomer === 0 || item.breakTimeEachCustomer ? item.breakTimeEachCustomer : 24 * 60,
          breakTimePurchasedCustomer: item.breakTimePurchasedCustomer || undefined,
          previewText: item.previewText || '',
          urlParam: item.urlParam || '',
          showOutOfStock: item.showOutOfStock || false,
          resumeItemIsEmpty: item.resumeItemIsEmpty || false,
          stage: item.stage || 'in_preparation',
          style: item.style || '',
          testAddress: '',
          messages: item.messages || '',
          // メール用の項目だが、必須項目のためダミー文字列を設定
          fromName: item.fromName || 'dummy',
          fromAddress: item.fromAddress || 'dummy',
          subject: item.subject || 'dummy',
        }}
        validationSchema={validationSchema}
        validate={(values) => {
          if (isTestDeliverySubmit && !values.testAddress) {
            return { testAddress: 'テスト送信時は必須項目です' }
          }
          return {}
        }}
        onSubmit={(values) => {
          if (isTestDeliverySubmit) {
            dispatch(abandonedCampaignAction.submitSendAbandonedCampaignTestMail(values))
            return
          }
          // formikでyup.transformが使えないようなので応急処置
          if (values.breakTimePurchasedCustomer === '0' || !values.breakTimePurchasedCustomer) {
            values = { ...values, breakTimePurchasedCustomer: null }
          }
          values = { ...values, aiOptimizedStatus: 'not_optimized' }
          if (submitAction === 'update') {
            dispatch(abandonedCampaignAction.submitUpdateAbandonedCampaign({ id: item.id, ...values, redirectPath: '/line_campaign_list' }))
          } else {
            dispatch(abandonedCampaignAction.submitCreateAbandonedCampaign({ ...values, redirectPath: '/line_campaign_list' }))
          }
        }}
      >
        {({
          values, submitForm,
        }) => (
          <Form noValidate>
            { abandonedCampaign.error && <Alert color="danger">{abandonedCampaign.error}</Alert> }
            <fieldset>
              <FormGroup>
                <Label require>キャンペーン名称</Label>
                <FieldSingle name="name" type="text" />
              </FormGroup>
              <FormGroup>
                <Label require>ターゲットアクション</Label>
                <FieldSingle
                  type="select"
                  name="abandonedType"
                  options={acc.abandonedTypes}
                  value={() => defaultTargetAction(item.templateTags, values)}
                  disabled={values.stage !== 'in_preparation'}
                />
                <FormText>
                  ステップメッセージ（2通目以降）を作成する際は、（後続）が付いたアクションを選択してください。
                </FormText>
              </FormGroup>
              { preCampaignSelect(parseInt(values.abandonedType)) }
            </fieldset>
            <legend className="mb-0">ターゲット顧客属性<HintIcon id="segmentPointTooltip" className="ml-1" style={{ 'fontSize': '14px' }} /></legend>
            <UncontrolledTooltip placement="right" target="segmentPointTooltip">
              ️施策に適した傾向を持つ顧客を配信対象に指定することで、成果指標（開封率・クリック率・完了率）を引き上げる効果が期待できます！
            </UncontrolledTooltip>
            <FormText className="mb-3" tag="span">
              セグメント別配信の設定メニューです。
            </FormText>
            <fieldset>
              <FormGroup>
                <Label require>購入履歴</Label>
                <FieldSingle type="select" name="purchaseHistory" options={acc.purchaseHistories} />
              </FormGroup>
              <FormGroup>
                <Label require>会員種別</Label>
                <FieldSingle type="select" name="customerType" options={acc.customerTypes} />
              </FormGroup>
              <FormGroup>
                <Label require>性別</Label>
                <FieldSingle type="select" name="gender" options={acc.gender} />
              </FormGroup>
              <FormGroup className="mb-0" check>
                <FieldSingle name="stopIfEmailExist" type="checkbox" option="メールアドレスを取得している顧客には送らない" />
              </FormGroup>
            </fieldset>
            <legend className="mb-3">配信スケジュール設定</legend>
            <fieldset>
              <FormGroup>
                <Label require>ターゲットアクションからの経過時間</Label>
                <HelpIcon id="scheduleTooltip" className="ml-1" />
                <UncontrolledTooltip placement="right" target="scheduleTooltip">
                  お客さまの⾏動の発⽣後どのくらいの時間にメッセージを配信するかを設定します。
                </UncontrolledTooltip>
                <Field type="select" name="schedule" as={Input} className="custom-select">
                  {scheduleTimes(isTestAccount, values.abandonedType, values.triggerCampaignId, abandonedCampaign.items).map((option, index) => (
                    <option key={index} value={option[1]}>{option[0]}</option>
                  ))}
                </Field>
              </FormGroup>
              { /* 旧管理画面に合わせて後続キャンペーンの場合はOFFにしているが、分岐させる必要があるか不明 */ }
              { !followingAbandonedTypes.includes(Number(values.abandonedType))
                && (
                  <FormGroup>
                    <Label require>一度LINEを送信した顧客への最低配信間隔</Label>
                    <HelpIcon id="breakTimeEachCustomerTooltip" className="ml-1" />
                    <FieldSingle type="select" name="breakTimeEachCustomer" options={breakTimeEachCustomer(isTestAccount, values.schedule)} />
                    <UncontrolledTooltip placement="right" target="breakTimeEachCustomerTooltip">
                      同じような内容のメッセージが連続で配信されないように、一定の期間中配信を停止するための設定です。
                    </UncontrolledTooltip>
                  </FormGroup>
                )
              }
              <FormGroup>
                <Label require>購入後配信停止期間設定</Label>
                <HelpIcon id="breakTimePurchasedCustomerTooltip" className="ml-1" />
                <UncontrolledTooltip placement="right" target="breakTimePurchasedCustomerTooltip">
                  購⼊されたお客さまに対してメッセージを配信を停止する期間を設定します。
                </UncontrolledTooltip>
                <FieldSingle type="select" name="breakTimePurchasedCustomer" options={acc.breakTimePurchasedCustomer} />
              </FormGroup>
            </fieldset>
            <legend className="mb-3">その他条件設定</legend>
            <fieldset>
              <FormGroup>
                <Label>URLパラメータ</Label>
                <HelpIcon id="urlParamTooltip" className="ml-1" />
                <UncontrolledTooltip placement="right" target="urlParamTooltip">
                  キャンペーン内のリンクに自動で付与するURLパラメータを設定できます。<br />
                  外部のアクセス解析ソフトの計測用パラメータの設定などにご利用ください。
                </UncontrolledTooltip>
                <FieldSingle name="urlParam" type="text" placeholder="param1=value2&param2=value2" />
              </FormGroup>
              <FormGroup check>
                <FieldGroup>
                  <Input name="showOutOfStock" type="checkbox" option="在庫のない商品を表示する" />
                  <HelpIcon id="showOutOfStockTooltip" className="ml-1" />
                  <UncontrolledTooltip placement="right" target="showOutOfStockTooltip">
                    在庫なしの商品であっても配信されるメッセージに表示したい場合にチェックを⼊れます。
                  </UncontrolledTooltip>
                </FieldGroup>
                <FormText className="mb-2">
                  ※ご利用にはあらかじめ在庫情報を連携するタグ（stock_status / stock_count）での在庫情報連携が必要です。
                </FormText>
                <FieldGroup>
                  <Input name="resumeItemIsEmpty" type="checkbox" option="商品情報が無い場合にも配信する" />
                  <HelpIcon id="resumeItemIsEmptyTooltip" className="ml-1" />
                  <UncontrolledTooltip placement="right" target="resumeItemIsEmptyTooltip">
                    お客様の閲覧したあるいはカートに⼊れた商品について、全て商品データに登録されていない場合でもメッセージを配信したい場合にチェックを⼊れます。
                  </UncontrolledTooltip>
                </FieldGroup>
              </FormGroup>
            </fieldset>
            <legend className="mb-3">LINE</legend>
            <fieldset className="mb-5">
              <FormGroup>
                <Label>LINEメッセージ内容</Label>
                <FieldCode name="messages" />
                <FormText>
                  LINEメッセージ内容を作成する場合は
                  <a href="https://developers.line.biz/flex-simulator/" target="_blank" rel="noopener noreferrer">
                    Flex Message Simulator
                  </a>
                  をご利用いただけます。
                </FormText>
                <FormText>
                  <ManualLink filename="mansys_line_setting/#52-line">
                    Flex Message Simulatorからメッセージ内容への反映方法はこちら。
                  </ManualLink>
                </FormText>
              </FormGroup>
              <div className="mb-2">
                <Button
                  color="secondary"
                  id="codeHelpToggler3"
                  onClick={() => {}}
                >
                  コードのヘルプを表示する
                </Button>
              </div>
              <UncontrolledCollapse toggler="#codeHelpToggler3" className="mb-5">
                <CampaignMailCodeHelp
                  campaignType={isCartOrCartFollowing(values.abandonedType) ? 'cart' : 'browser'}
                  messageType="line"
                />
              </UncontrolledCollapse>
              { abandonedCampaign.processResult.message && abandonedCampaign.processResult.message.match(/LINE/)
              && (
                <Alert color="danger">
                LINE本文の記述に誤りがあるため、保存に失敗しました。
                </Alert>
              )}
            </fieldset>
            <FormGroup>
              <Label>
                テスト送信先LINE ID
              </Label>
              <FieldGroup>
                <Input name="testAddress" type="text" />
                <InputGroupAddon addonType="append">
                  <Button
                    color="info"
                    onClick={(e) => {
                      isTestDeliverySubmit = true
                      submitForm()
                    }}
                  >
                    テスト送信
                  </Button>
                </InputGroupAddon>
              </FieldGroup>
              <FormText>カンマ区切りで複数入力可能</FormText>
              <FormText>※LINE文面に表示されるアイテム情報などは、ランダムに抽出されたものが表示されます。</FormText>
              <FormText>
                <a href={gc.lineIdCheckUrl} target="_blank" rel="noopener noreferrer">
                  LINE IDを確認する（クリックするとLINE IDが表示されます）
                </a>
              </FormText>
            </FormGroup>
            <div className="text-center">
              <Button
                color="primary"
                size="lg"
                onClick={(e) => {
                  isTestDeliverySubmit = false
                  submitForm()
                }}
              >
                保存
              </Button>
            </div>
          </Form>
        )}
      </Formik>
    </Loading>
  )
}

const isCartOrCartFollowing = abandonedType => (
  [abandonedTypes.cart, abandonedTypes.notPurchasedAfterCampaign].includes(Number(abandonedType))
)

/**
 * 配信スケジュールの選択肢設定用メソッド。
 * 先発キャンペーンがある場合は配信スケジュールに制限がかかる。
 * @param  {Boolean} isTestAccount        検証用アカウント判定
 * @param  {Integer} triggerCampaignId    先発キャンペーンID
 * @param  {Array}   allAbandonedCampaign すべての放棄型キャンペーン
 * @return {Array}                        表示すべき配信スケジュール一覧
 */

const scheduleTimes = (isTestAccount, abandonedType, triggerCampaignId, allAbandonedCampaign) => {
  const rawSheduleTimes = isTestAccount ? acc.scheduleTimesForTestAccount : acc.scheduleTimes
  if (!followingAbandonedTypes.includes(Number(abandonedType)) || !triggerCampaignId) { return rawSheduleTimes }
  const triggerCampaign = allAbandonedCampaign.find(campaign => campaign.id === Number(triggerCampaignId))
  return rawSheduleTimes.filter(schedule => schedule[1] > Number(triggerCampaign.schedule))
}

/**
 * 一度メッセージを送信した顧客への最低配信間隔の選択肢設定用メソッド。
 * 配信スケジュールに応じて制限がかかる。
 * @param  {Boolean} isTestAccount  検証アカウント判定
 * @param  {Integer} scheduleTime   現在選択されている配信スケジュール
 * @return {Array}                  表示すべき一度メッセージを送信した顧客への最低配信間隔一覧
 */

const breakTimeEachCustomer = (isTestAccount, scheduleTime) => {
  if (isTestAccount) {
    // 旧管理画面に合わせてテストアカウントはフィルタリングを行わない
    return acc.breakTimeEachCustomerForTestAccount
  }
  return acc.breakTimeEachCustomer.filter(bre => (bre[1] >= Number(scheduleTime)))
}

const defaultTargetAction = (templateTags, values) => {
  if (templateTags) {
    const mappingNames = acc.tagMappingAbandonedTypes.map(m => m.name)
    const mappedTag = templateTags.filter(tag => mappingNames.includes(tag.name))[0]
    if (mappedTag) {
      const abandonedType = acc.tagMappingAbandonedTypes.filter(m => m.name === mappedTag.name)[0].value
      values.abandonedType = abandonedType
      return abandonedType
    }
  }
  return values.abandonedType
}

const select = ({ abandonedCampaign, company }, { campaignId, submitAction }) => {
  let campaignContentOverride = {}
  if (campaignId) {
    campaignContentOverride = abandonedCampaign.items.find(obj => obj.id === campaignId) || {}
  }
  const item = { ...campaignContentOverride }
  return {
    abandonedCampaign, company, item, submitAction,
  }
}

export default connect(select)(OldAbandonedCampaignForm)
